import React from "react";
import { SdFlcReactRouterPages } from "@sdflc/react-router-pages";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

import {
  ThemeProvider,
  AppProvider,
  NetworkProvider,
  useApp,
  DisplayLayoutProvider,
  RoomTypesProvider,
  VenuesProvider,
  CacheProvider,
  CategoriesProvider,
  GoogleMapsProvider,
} from "@/contexts";
import { ContentBlockStateProvider } from "@/contexts/ContentBlockStateContext";
import { LoadingOverlay, Favicon } from "@/components/generic";

const AppContent: React.FC = () => {
  const { siteMap, userRoles } = useApp();

  return <SdFlcReactRouterPages siteMap={siteMap} roles={userRoles} />;
};

const AuthenticatedApp: React.FC = () => {
  const { user, isAuthenticated, isInitializing } = useApp();

  // Show loading while initializing
  if (isInitializing) {
    return <LoadingOverlay visible loadingText="Initializing..." />;
  }

  return (
    <RoomTypesProvider
      options={{
        pageSize: 100,
        autoLoad: true,
        isAuthenticated,
      }}
    >
      <VenuesProvider
        options={{
          pageSize: 100,
          autoLoad: true,
          isAuthenticated,
          userId: user?.id,
        }}
      >
        <DisplayLayoutProvider
          options={{
            pageSize: 100,
            autoLoad: true,
            isAuthenticated,
            userId: user?.id,
          }}
        >
          <CategoriesProvider
            options={{
              pageSize: 100,
              autoLoad: true,
              isAuthenticated,
              userId: user?.id,
            }}
          >
            <AppContent />
          </CategoriesProvider>
        </DisplayLayoutProvider>
      </VenuesProvider>
    </RoomTypesProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Favicon />
      <NetworkProvider
        options={{
          checkInterval: 30000,
          testUrl: import.meta.env.VITE_GATEWAY_ENDPOINT,
          testTimeout: 5000,
          enableConnectivityTest: true,
        }}
      >
        <CacheProvider>
          <AppProvider>
            <GoogleMapsProvider>
              <ContentBlockStateProvider>
                <AuthenticatedApp />
              </ContentBlockStateProvider>
            </GoogleMapsProvider>
          </AppProvider>
        </CacheProvider>
      </NetworkProvider>
    </ThemeProvider>
  );
}

export default App;